import type { RebateRule, RebateRuleProductGroup } from '@erp/shared-types';
import type { RebateLineItem } from '@/api/rebates/useRebateLineItems';

/**
 * Evaluate if a line item qualifies/affects a rule based on product group rules
 * This mirrors the backend logic in RebateDataLoader.evaluateProductGroupRules
 */
export function evaluateProductGroupRules(
  rule: RebateRule,
  productGroupId: string | null,
): { qualifying: boolean; affected: boolean } {
  const qualifying = isProductGroupMatching(rule, productGroupId, 'qualifying');
  const affected = isProductGroupMatching(rule, productGroupId, 'affected');

  return { qualifying, affected };
}

/**
 * Check if product group matches rule criteria
 */
function isProductGroupMatching(
  rule: RebateRule,
  productGroupId: string | null,
  ruleType: 'qualifying' | 'affected',
): boolean {
  // Non-rebatable items (null product group) never match any rule
  // This matches the backend logic in RebateDataLoader.evaluateProductGroupRules
  if (productGroupId === null) {
    return false;
  }

  const productGroupIds =
    ruleType === 'qualifying'
      ? rule.qualifyingProductGroups?.map((pg) => pg.id) || []
      : rule.affectedProductGroups?.map((pg) => pg.id) || [];

  const inclusionType =
    ruleType === 'qualifying'
      ? rule.qualifyingInclusionType || 'inclusive'
      : rule.affectedInclusionType || 'inclusive';

  if (inclusionType === 'inclusive') {
    // Inclusive: item must be in specified groups (or all groups if none specified)
    return (
      productGroupIds.length === 0 || productGroupIds.includes(productGroupId)
    );
  } else {
    // Exclusive: item must NOT be in specified groups
    return !productGroupIds.includes(productGroupId);
  }
}

/**
 * Get applicable divisions for a rule
 */
export function getApplicableDivisions(rule: RebateRule): Set<string> {
  if (!rule.divisions || rule.divisions.length === 0) {
    return new Set(); // Empty set means all divisions
  }
  return new Set(rule.divisions.map((d) => d.id));
}

/**
 * Check if a line item date falls within a date range
 */
function isLineItemInDateRange(
  lineItemDate: string,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): boolean {
  // If no date constraints, include all
  if (!startDate && !endDate) {
    return true;
  }

  const itemDate = new Date(lineItemDate);

  if (startDate) {
    const start = new Date(startDate);
    // Line item must be on or after the start date
    if (itemDate < start) {
      return false;
    }
  }

  if (endDate) {
    const end = new Date(endDate);
    // Line item must be on or before the end date
    if (itemDate > end) {
      return false;
    }
  }

  return true;
}

/**
 * Calculate volume totals for a rule from line items
 * Returns both gross (pre-discount) and discounted volumes
 *
 * @param rule - The rebate rule
 * @param lineItems - All line items for the agreement
 * @param agreementStartDate - Agreement start date (fallback if rule has no startDate)
 * @param agreementEndDate - Agreement end date (fallback if rule has no endDate)
 */
export function calculateRuleVolumes(
  rule: RebateRule,
  lineItems: RebateLineItem[],
  agreementStartDate?: string,
  agreementEndDate?: string,
): {
  totalQualifyingVolume: number;
  totalAffectedVolume: number;
  discountedQualifyingVolume: number;
  discountedAffectedVolume: number;
  discountPercentage: number;
} {
  if (rule.isManual) {
    return {
      totalQualifyingVolume: 0,
      totalAffectedVolume: 0,
      discountedQualifyingVolume: 0,
      discountedAffectedVolume: 0,
      discountPercentage: 0,
    };
  }

  // Determine effective date range for this rule
  // Rule dates take precedence, fall back to agreement dates
  const effectiveStartDate = rule.startDate ?? agreementStartDate;
  const effectiveEndDate = rule.endDate ?? agreementEndDate;

  const applicableDivisions = getApplicableDivisions(rule);
  let totalQualifying = 0;
  let totalAffected = 0;

  for (const lineItem of lineItems) {
    // Check if line item is within the rule's active date range
    if (
      !isLineItemInDateRange(
        lineItem.date,
        effectiveStartDate,
        effectiveEndDate,
      )
    ) {
      continue;
    }

    // Check division scope
    if (
      applicableDivisions.size > 0 &&
      !applicableDivisions.has(lineItem.divisionId)
    ) {
      continue;
    }

    // Evaluate product group rules
    const { qualifying, affected } = evaluateProductGroupRules(
      rule,
      lineItem.productGroupId,
    );

    if (qualifying) {
      totalQualifying += lineItem.value;
    }

    if (affected) {
      totalAffected += lineItem.value;
    }
  }

  // Apply early payment discount
  const discountPct = rule.discountPercentage
    ? parseFloat(rule.discountPercentage)
    : 0;
  const discountFactor = Math.max(0, Math.min(1, 1 - discountPct / 100));

  return {
    totalQualifyingVolume: totalQualifying,
    totalAffectedVolume: totalAffected,
    discountedQualifyingVolume: totalQualifying * discountFactor,
    discountedAffectedVolume: totalAffected * discountFactor,
    discountPercentage: discountPct,
  };
}
